import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import JobCard from '../common/JobCard';
import { getSoraPreview } from '../../services/soraService';

const VideoModal = ({ src, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!src) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[85vh] aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-md transition-colors hover:bg-white/20"
        >
          <FontAwesomeIcon icon="xmark" className="text-sm" />
        </button>
        <video src={src} className="h-full w-full object-contain" controls autoPlay playsInline />
      </div>
    </div>
  );
};

const RecentJobs = ({
  jobs = [],
  loading = false,
  onDownload,
  onCopy,
  onDelete,
  onRetry,
  onUpscale,
  onRefresh,
  downloadingId,
  upscaleCost = 15,
  maxDisplay = 6,
  showViewAll = true,
}) => {
  const navigate = useNavigate();
  const [fullscreenVideo, setFullscreenVideo] = useState(null);
  const [previewCache, setPreviewCache] = useState({});
  const previewFetchedRef = useRef({});

  // Fetch preview pour un job
  const fetchJobPreview = useCallback(async (job) => {
    if (!job?.source_url) return;
    const cacheKey = job.sora_id || job.source_url;
    
    // DÃ©jÃ  fetchÃ© ou en cours
    if (previewFetchedRef.current[cacheKey]) return;
    previewFetchedRef.current[cacheKey] = true;

    try {
      const data = await getSoraPreview(job.source_url);
      const payload = {
        thumbnailUrl: data.thumbnail_url || null,
        previewUrl: data.preview_url || null,
      };
      setPreviewCache((prev) => ({ ...prev, [cacheKey]: payload }));
    } catch (err) {
      console.error('[RecentJobs] Failed to fetch preview:', err);
      // MÃªme en erreur, on marque comme "tentÃ©" pour Ã©viter retry infini
    }
  }, []);

  // Auto-fetch previews pour les jobs affichÃ©s
  useEffect(() => {
    jobs.slice(0, maxDisplay).forEach((job) => {
      const cacheKey = job.sora_id || job.source_url;
      if (!previewFetchedRef.current[cacheKey]) {
        fetchJobPreview(job);
      }
    });
  }, [jobs, maxDisplay, fetchJobPreview]);

  const groupedJobs = useMemo(() => {
    const groups = {};
    jobs.forEach((job) => {
      const id = job.sora_id || job.source_url;
      if (!id) return;

      if (!groups[id]) {
        groups[id] = {
          id,
          sora_id: job.sora_id,
          source_url: job.source_url,
          jobs: [],
        };
      }
      groups[id].jobs.push(job);
    });

    return Object.values(groups)
      .sort((a, b) => {
        const dateA = Math.max(...a.jobs.map((j) => new Date(j.created_at).getTime()));
        const dateB = Math.max(...b.jobs.map((j) => new Date(j.created_at).getTime()));
        return dateB - dateA;
      })
      .slice(0, maxDisplay);
  }, [jobs, maxDisplay]);

  const handleViewAll = () => navigate('/upscale?tab=history');

  return (
    <section className="space-y-4">
      <VideoModal src={fullscreenVideo} onClose={() => setFullscreenVideo(null)} />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Derniers jobs</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Historique de tes traitements Sora
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-300 transition-colors"
            >
              <FontAwesomeIcon icon="sync" className={loading ? 'animate-spin' : ''} />
              Actualiser
            </button>
          )}
          {showViewAll && jobs.length > maxDisplay && (
            <button
              type="button"
              onClick={handleViewAll}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-300 hover:underline"
            >
              Voir tout
              <FontAwesomeIcon icon="arrow-right" className="text-[10px]" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].slice(0, maxDisplay).map((key) => (
            <div
              key={key}
              className="h-28 rounded-2xl bg-slate-100 animate-pulse dark:bg-white/5"
            />
          ))}
        </div>
      ) : groupedJobs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-white/10 bg-white/60 dark:bg-slate-900/30 p-6 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center">
              <FontAwesomeIcon icon="video" className="text-slate-400 text-xl" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                Aucun job pour le moment
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Lance ton premier upscale pour le voir apparaÃ®tre ici
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/upscale')}
              className="mt-2 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors"
            >
              <FontAwesomeIcon icon="infinity" />
              Lancer un upscale
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {groupedJobs.map((group) => {
            const cacheKey = group.sora_id || group.source_url;
            return (
              <JobCard
                key={group.id}
                group={group}
                compact={true}
                onDownload={onDownload}
                onCopy={onCopy}
                onDelete={onDelete}
                onRetry={onRetry}
                onUpscale={onUpscale}
                onExpand={setFullscreenVideo}
                downloadingId={downloadingId}
                upscaleCost={upscaleCost}
                previewData={previewCache[cacheKey]}
                onHoverPreview={(job) => fetchJobPreview(job)}
              />
            );
          })}
        </div>
      )}
    </section>
  );
};

export default RecentJobs;

